import { getI18n } from '@/main';
import { useMapStoreData } from '@/stores/index';

const ITINERARY_API_URL = import.meta.env.VITE_ITINERARY_API_URL;
const PWA_APP_URL = import.meta.env.VITE_PWA_APP_URL;

function hashCode(str: string): number {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash |= 0;
	}
	return hash;
}

function formatRequest(backinterface: string, method: string, parameters: object): string {
	const request = { interface: backinterface, method, parameters };
	return JSON.stringify({ request });
}

/**
 * Saves an itinerary for the given POI ids (e.g. module.pois) and returns the URL to encode in the QR code.
 * Uses the same API and URL format as the favorites itinerary QR.
 */
export async function saveItineraryForPois(poiIds: string[]): Promise<string | null> {
	if (!poiIds?.length) return null;
	const mapStore = useMapStoreData();
	const markers: Record<string, any> = mapStore.markers ?? {};
	let pathType = mapStore.options?.directionsType ?? 'walking';
	if (['driving', 'walking', 'cycling'].indexOf(pathType) === -1) pathType = 'walking';

	const i18n = getI18n();
	const pois: any[] = [];
	for (const id of poiIds) {
		const marker = markers[id] ?? markers[String(id)];
		if (marker) {
			const translated = {
				...marker,
				properties: {
					...marker.properties,
					title: i18n?.global.t(id + '.title') || marker.properties.title,
					description: i18n?.global.t(id + '.description') || marker.properties.description,
				},
			};
			pois.push(translated);
		}
	}
	if (pois.length === 0) return null;

	// Same route format as Favorites: single-point route (device location or first POI)
	const { device_location_lng, device_location_lat } = mapStore.options ?? {};
	const routeCoordinate =
		device_location_lng != null && device_location_lat != null
			? [device_location_lng, device_location_lat]
			: pois[0].geometry.coordinates;
	const pathSource = {
		routes: [{ geometry: { coordinates: routeCoordinate } }],
	};

	const hashInt = hashCode(poiIds.join('-'));
	const nameItinerary = pathType + '-' + hashInt;
	const myOptions: Record<string, unknown> = {
		destination: mapStore.destinationId,
		lang: mapStore.currentLanguage,
		repoUrl: mapStore.publicUrl + 'repo/' + mapStore.destinationId + '/',
	};
	// MapCopy: use translated module title when set, otherwise fall back to active_path POI title
	const moduleTitleKey = mapStore.mapCopyModuleTitleKey;
	if (moduleTitleKey) {
		myOptions.title = getI18n()?.global.t(moduleTitleKey) ?? '';
	} else if (mapStore.activePathPoiId) {
		myOptions.title = getI18n()?.global.t(mapStore.activePathPoiId + '.title') ?? '';
	}

	try {
		const jsonRequest = formatRequest('LocationRequests', 'saveItinerary', {
			name: nameItinerary,
			pois,
			route: pathSource.routes[0],
			options: myOptions,
		});
		const response = await fetch(ITINERARY_API_URL, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: jsonRequest,
			credentials: 'omit',
		});
		if (!response.ok) return null;
		return PWA_APP_URL + '?' + nameItinerary;
	} catch {
		return null;
	}
}
